import { startOfDay } from 'date-fns';

import { getFormated24hTimeFromDate } from '@core/utils/date-utils';

import { CalendarEvent } from '@features/components/visualization/calendar/models/calendar.models';

/**
 * Vertical scale of the day and week grids. Fixed rather than measured: the
 * grid scrolls inside its pane instead of squashing 24 hours into whatever
 * height the pane happens to have, so every placement below is a pure function
 * of the clock rather than of the layout.
 */
export const HOUR_PX = 38;

export const MINUTES_IN_DAY = 1440;

/** Height of one full day column, in pixels. */
export const DAY_HEIGHT_PX = (MINUTES_IN_DAY / 60) * HOUR_PX;

/** Pixels per minute, the scale the drag/resize math works in. */
export const PIXELS_PER_MINUTE = HOUR_PX / 60;

/** Horizontal placement of one event within its overlap cluster, in percent. */
export interface ColumnLayout {
  width: number;
  left: number;
}

/**
 * Width and left offset for `column` of `columns` overlapping events.
 *
 * Concurrent events tile the day's width evenly, side by side: none is ever
 * drawn over another, so every block shows its own title, times and recurrence
 * mark in full. The cost is width — three concurrent events in a seven-column
 * week get a third each — which is the trade accepted for never hiding an
 * event behind a neighbour.
 */
export function columnLayout(column: number, columns: number): ColumnLayout {
  if (columns <= 1) {
    return { width: 100, left: 0 };
  }

  const width = 100 / columns;

  return { width, left: column * width };
}

/** Which edges of a block are a cut through the event rather than its real end. */
export interface DaySegment {
  continuesBefore: boolean;
  continuesAfter: boolean;
}

/**
 * Whether the slice of `window` visible on `day` is clipped at either column
 * edge. A clipped edge is drawn open (no border, no radius) so a window
 * crossing midnight reads as one event cut in two rather than two events.
 */
export function daySegment(window: { start: Date; end: Date }, day: Date): DaySegment {
  const dayStart = startOfDay(day);
  const dayEnd = new Date(dayStart.getTime() + MINUTES_IN_DAY * 60_000);

  return {
    continuesBefore: window.start < dayStart,
    continuesAfter: window.end > dayEnd
  };
}

/**
 * The colour an event's rail, cutoff and legend tint are all derived from.
 * `borderColor` is the schedule's own hue; `mainColor` is the map-marker fill
 * it falls back to.
 */
export function eventColor(event: CalendarEvent): string | null {
  return event.borderColor ?? event.mainColor ?? null;
}

/** Fill of an event block: the schedule's hue, barely there. */
export function tintFill(color: string): string {
  return `color-mix(in oklch, ${color} 13%, var(--bg-elevated))`;
}

/** Fill of the region after the cutoff: the same hue, deepened. */
export function tintCutoff(color: string): string {
  return `color-mix(in oklch, ${color} 34%, var(--bg-elevated))`;
}

/**
 * Ink for text and hairlines drawn on a block's own tint: the event's hue
 * pushed towards the text colour so it stays legible in both themes without
 * introducing a hue that competes with the red now-line.
 */
export function eventInk(color: string): string {
  return `color-mix(in oklch, ${color} 88%, var(--text-primary))`;
}

/**
 * Fill of a badge that has to carry text in `--bg-elevated`: the event's hue
 * pulled most of the way to `--text-primary`, so the badge takes its lightness
 * from the theme's own foreground and stays legible whatever the hue.
 *
 * `eventInk` cannot serve here. At 88% hue the badge is as light as the
 * brightest entries of the marker palette — `amber #ffc107`, `orange #ff9800`,
 * `lightGreen #8bc34a` — which in light mode leaves near-white text on a pale
 * background at around 1.7:1, i.e. the deadline is unreadable on exactly the
 * events whose colour is brightest.
 */
export function badgeFill(color: string): string {
  return `color-mix(in oklch, ${color} 22%, var(--text-primary))`;
}

/**
 * Wall-clock time, e.g. `14:30`. Pinned to a 24-hour cycle rather than left to
 * the locale, so the block's mono readout lines up digit-for-digit with the
 * hour gutter and the cutoff badge instead of turning into `02:30 PM` for a
 * viewer whose browser is set to en-US.
 */
export function formatClock(date: Date): string {
  return getFormated24hTimeFromDate(date);
}

/** `14:30 – 18:00`, shared by the event block, its tooltip and the month pills. */
export function formatTimeRange(start: Date, end: Date): string {
  return `${formatClock(start)} – ${formatClock(end)}`;
}
